/**
 * Keracraft Sale Display v3 — Streichpreis-Überbrückung (ESN-Muster).
 *
 * Zeigt rabattierte Preise im Lücken-Fenster "Rabattcode aktiviert +
 * Warenkorb leer", in dem Shopify/Regios nichts anzeigen können.
 * Prozent-Quelle: data-kc-sale-percent (aus snippets/kc-sale-percent.liquid).
 *
 * v3-Neuerungen für flüssige UX (wie ESN):
 *  - FRÜH-START: Der Aktiv-Zustand wird in sessionStorage gecacht. Das
 *    Script lädt OHNE defer im <head>, liest den Cache synchron und
 *    überbrückt Preis-Elemente bereits während des Seitenaufbaus
 *    (Streaming-Observer) — kein sichtbares Preis-Umspringen mehr.
 *    Der echte Warenkorb-Zustand wird danach per /cart.js verifiziert
 *    und der Cache korrigiert.
 *  - LIVE-REFRESH: window.KCSaleDisplay.refresh(cart?) wertet den Zustand
 *    neu aus und überbrückt bzw. stellt Originalpreise wieder her — ohne
 *    Reload. Wird vom Cart-Wächter und den Bannern im Lücken-Fenster
 *    aufgerufen (Drawer bleibt offen, Preise wechseln im Hintergrund).
 *    refresh() liefert ein Promise auf {active: bool}.
 *
 * Selbstheilung (live ermittelt): Regios setzt die Anzeige im Lücken-
 * Fenster aktiv zurück und versteckt Streichpreise per "visually hidden"-
 * Muster (1x1px, geclippt) aus einem Cross-Origin-Stylesheet. Die Engine
 * speichert ihre Werte als data-Attribute und setzt die Anzeige bei jeder
 * Abweichung erneut durch (Observer + Intervall); geschrieben wird nur
 * bei Abweichung — kein Endlos-Pingpong.
 *
 * Regios-Rundung: RABATT-Betrag abrunden (24,99 € -20 % -> 20,00 €).
 * Rabatt-Basis: sichtbarer Vergleichspreis (compare at), falls vorhanden.
 * Die Metaobjekt-Prozente MÜSSEN mit der Regios-Rabattlogik synchron sein.
 */
(function () {
  'use strict';

  if (window.KCSaleDisplay) return;

  var STATE_KEY = 'kcSaleDisplayState';
  var activeMode = false;
  var activeCode = '';
  var watchersInstalled = false;

  // ---------- Helfer ----------

  function formatMoney(cents) {
    if (window.theme && theme.Shopify && theme.Shopify.formatMoney) {
      return theme.Shopify.formatMoney(cents, theme.moneyFormat || '€{{amount_with_comma_separator}}');
    }
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function parseCents(text) {
    var m = String(text).replace(/ /g, ' ').match(/(\d{1,3}(?:\.\d{3})*|\d+),(\d{2})/);
    if (!m) return null;
    return parseInt(m[1].replace(/\./g, ''), 10) * 100 + parseInt(m[2], 10);
  }

  function discounted(cents, percent) {
    return cents - Math.floor(cents * percent / 100);
  }

  function readState() {
    try { return JSON.parse(sessionStorage.getItem(STATE_KEY)); } catch (e) { return null; }
  }

  function writeState(state) {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (e) {}
  }

  // ---------- Überbrücken ----------

  function bridgeContainer(container, percent) {
    var saleEl = container.querySelector('.regios-dopp-generic-price-item--sale');
    var regularEl = container.querySelector('.regios-dopp-generic-price-item--regular');
    if (!saleEl || !regularEl) return;

    if (!saleEl.hasAttribute('data-kc-sale')) {
      // Originalpreis IMMER bevorzugt aus dem servergerenderten Attribut
      // lesen (data-kc-price-cents aus Liquid bzw. theme.js) — NIE aus dem
      // angezeigten Text raten, wenn das Attribut da ist: Der Text kann in
      // dem Moment bereits von Regios rabattiert worden sein, und ein
      // Parse-Fehlgriff würde doppelt rabattieren (24,99 -> 20,00 -> 16,00).
      var priceCents = Number(saleEl.getAttribute('data-kc-price-cents')) || parseCents(saleEl.textContent);
      if (!priceCents) return;
      var baseCents = priceCents;
      var compareAttr = Number(saleEl.getAttribute('data-kc-compare-cents')) || 0;
      if (compareAttr > priceCents) {
        baseCents = compareAttr;
      } else if (!saleEl.hasAttribute('data-kc-price-cents')) {
        // Fallback nur ohne Server-Attribute: sichtbaren Vergleichspreis nutzen
        var themeCompare = container.querySelector('[data-compare-price] .product-price__compare, .product-price__compare:not(.regios-dopp-generic-price-item--regular)');
        if (themeCompare && themeCompare.offsetHeight > 0) {
          var compareCents = parseCents(themeCompare.textContent);
          if (compareCents && compareCents > priceCents) baseCents = compareCents;
        }
      }
      var saleCents = discounted(baseCents, percent);
      if (saleCents >= priceCents) return;
      saleEl.setAttribute('data-kc-price', priceCents);
      saleEl.setAttribute('data-kc-base', baseCents);
      saleEl.setAttribute('data-kc-sale', saleCents);
    }

    var price = Number(saleEl.getAttribute('data-kc-price'));
    var base = Number(saleEl.getAttribute('data-kc-base'));
    var sale = Number(saleEl.getAttribute('data-kc-sale'));
    if (!sale || !price) return;

    var saleTxt = formatMoney(sale);
    if (saleEl.textContent.trim() !== saleTxt.trim()) saleEl.textContent = saleTxt;

    var baseTxt = formatMoney(base);
    if (regularEl.textContent.trim() !== baseTxt.trim()) regularEl.textContent = baseTxt;
    if (regularEl.offsetHeight < 5) {
      // Gegen Regios' "visually hidden"-Muster: alle beteiligten
      // Eigenschaften inline mit !important zurückdrehen.
      var unhide = {
        'display': 'inline', 'position': 'static', 'width': 'auto',
        'height': 'auto', 'clip': 'auto', 'clip-path': 'none',
        'overflow': 'visible', 'margin': '0', 'padding': '0',
        'white-space': 'normal'
      };
      for (var prop in unhide) {
        regularEl.style.setProperty(prop, unhide[prop], 'important');
      }
    }

    container.classList.add('regios-dopp-generic-price--on-sale');

    var unitEl = container.querySelector('.unit-price__price');
    if (unitEl) {
      var unitBase = Number(unitEl.getAttribute('data-unit-price-cents'));
      if (unitBase) {
        var unitTxt = formatMoney(Math.round(unitBase * sale / price));
        if (unitEl.textContent.trim() !== unitTxt.trim()) unitEl.innerHTML = unitTxt;
      }
    }
  }

  // Original-Anzeige wiederherstellen (Code wurde entfernt)
  function unbridgeContainer(container) {
    var saleEl = container.querySelector('.regios-dopp-generic-price-item--sale');
    var regularEl = container.querySelector('.regios-dopp-generic-price-item--regular');
    if (!saleEl || !saleEl.hasAttribute('data-kc-sale')) return;

    var price = Number(saleEl.getAttribute('data-kc-price'));
    var base = Number(saleEl.getAttribute('data-kc-base'));
    saleEl.textContent = formatMoney(price);
    saleEl.removeAttribute('data-kc-price');
    saleEl.removeAttribute('data-kc-base');
    saleEl.removeAttribute('data-kc-sale');

    if (regularEl) {
      if (base > price) {
        // Produkt hatte echten Vergleichspreis -> sichtbar lassen
        regularEl.textContent = formatMoney(base);
      } else {
        regularEl.textContent = '';
        regularEl.setAttribute('style', 'display: none');
      }
    }
    container.classList.remove('regios-dopp-generic-price--on-sale');

    var unitEl = container.querySelector('.unit-price__price');
    if (unitEl) {
      var unitBase = Number(unitEl.getAttribute('data-unit-price-cents'));
      if (unitBase) unitEl.innerHTML = formatMoney(unitBase);
    }
  }

  // ---------- Flächen ----------

  function fillDescription() {
    var info = document.querySelector('.regios-dopp-generic-product-info[data-kc-sale-percent]');
    if (!info) return;
    var percent = parseInt(info.getAttribute('data-kc-sale-percent'), 10);
    var desc = info.querySelector('.regios-dopp-description');
    if (!desc || !percent || !activeCode) return;
    // Schon mit dem AKTUELLEN Code befüllt? Dann nichts tun. Bei einem
    // Code-WECHSEL (überschreiben im Drawer) muss der Chip aber neu
    // geschrieben werden — sonst zeigt er den alten Code bis zum Reload.
    if (desc.getAttribute('data-kc-desc-code') === activeCode.toUpperCase()) return;
    // Fremd befüllt (z. B. von Regios selbst) und nicht von uns -> nicht anfassen
    if (desc.textContent.trim() && desc.getAttribute('data-kc-desc') !== '1') return;
    desc.setAttribute('data-kc-desc', '1');
    desc.setAttribute('data-kc-desc-code', activeCode.toUpperCase());
    desc.setAttribute('style', 'display:inline-flex;align-items:center;gap:0.45em;background:#E83168;color:#fff;font-size:0.85rem;font-weight:600;line-height:1;padding:0.55em 0.95em;border-radius:999px;margin-top:0.75em;text-transform:uppercase;letter-spacing:0.03em;white-space:nowrap;');
    desc.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="width:1em;height:1em;flex:none;">' +
      '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.83z"></path>' +
      '<circle cx="7" cy="7" r="1.4" fill="currentColor" stroke="none"></circle></svg> ' +
      '-' + percent + '% CODE: ' + activeCode.toUpperCase().replace(/</g, '');
  }

  function clearDescription() {
    var desc = document.querySelector('.regios-dopp-description[data-kc-desc="1"]');
    if (!desc) return;
    desc.removeAttribute('data-kc-desc');
    desc.removeAttribute('data-kc-desc-code');
    desc.removeAttribute('style');
    desc.innerHTML = '';
  }

  function eachContainer(fn) {
    var info = document.querySelector('.regios-dopp-generic-product-info[data-kc-sale-percent]');
    if (info) {
      var percent = parseInt(info.getAttribute('data-kc-sale-percent'), 10);
      var container = info.querySelector('.product-detail__price.regios-dopp-generic-price-container');
      if (percent && container) {
        try { fn(container, percent); } catch (e) {}
      }
    }
    var cards = document.querySelectorAll('.product-block[data-kc-sale-percent]');
    for (var i = 0; i < cards.length; i++) {
      try {
        var p = parseInt(cards[i].getAttribute('data-kc-sale-percent'), 10);
        var c = cards[i].querySelector('.regios-dopp-generic-price-container');
        if (p && c) fn(c, p);
      } catch (e) {}
    }
  }

  function pass() {
    if (!activeMode) return;
    eachContainer(bridgeContainer);
    fillDescription();
  }

  function unbridgeAll() {
    eachContainer(function (container) { unbridgeContainer(container); });
    clearDescription();
  }

  // ---------- Beobachtung (Selbstheilung + Streaming beim Seitenaufbau) ----------

  function installWatchers() {
    if (watchersInstalled) return;
    watchersInstalled = true;
    var pending = null;
    new MutationObserver(function () {
      if (!activeMode) return;
      clearTimeout(pending);
      pending = setTimeout(pass, 120);
    }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
    setInterval(pass, 1500);
  }

  // ---------- Zustand auswerten ----------

  function applyState(active, code) {
    activeCode = code || '';
    if (active && !activeMode) {
      activeMode = true;
      installWatchers();
      pass();
    } else if (active && activeMode) {
      pass();
    } else if (!active && activeMode) {
      activeMode = false;
      unbridgeAll();
    } else {
      unbridgeAll(); // falls Früh-Start fälschlich überbrückt hat
    }
  }

  function evaluate(cart) {
    var codes = cart.discount_codes || [];
    // Aktiv bei jedem gespeicherten Code. Bei gefülltem Warenkorb können
    // wir strenger sein: Dort ist Shopifys "applicable"-Flag aussagekräftig
    // (leerer Warenkorb kann es nicht bewerten) — ungültige Codes zeigen
    // dann keine Rabatte an.
    var active;
    if (cart.item_count > 0) {
      active = codes.some(function (c) { return c.applicable; });
    } else {
      active = codes.length > 0;
    }
    var code = codes.length ? (codes[0].code || '') : '';
    writeState({ active: active, code: code });
    applyState(active, code);

    // Zustandswechsel an alle interessierten Bausteine funken (Banner
    // blendet sich ein/aus, Drawer-Widget aktualisiert seinen Code-Chip) —
    // ersetzt, was früher der Seiten-Reload nebenbei erledigt hat.
    try {
      window.dispatchEvent(new CustomEvent('kc:sale-state', {
        detail: { active: active, code: code, codes: codes, itemCount: cart.item_count }
      }));
    } catch (e) {}

    return { active: active, code: code };
  }

  function refresh(cart) {
    if (cart && typeof cart.item_count === 'number') {
      return Promise.resolve(evaluate(cart));
    }
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(evaluate);
  }

  window.KCSaleDisplay = { refresh: refresh };

  // ---------- Start ----------

  // 1) Früh-Start aus dem Session-Cache: überbrückt schon während des
  //    Seitenaufbaus (Script lädt ohne defer im <head>).
  var cached = readState();
  if (cached && cached.active) {
    activeMode = true;
    activeCode = cached.code || '';
    installWatchers(); // Streaming: bridged Elemente, sobald sie erscheinen
    pass();
  }

  // 2) Verifikation gegen den echten Warenkorb (korrigiert den Cache)
  function boot() { refresh(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
